import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ChevronLeft, BookOpen, Clock, User, Share2 } from 'lucide-react';
import { Breadcrumb } from '../../components/common/Breadcrumb';

export const ResourceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: resource, isLoading, error } = useQuery({
    queryKey: ['public-resource-detail', slug],
    queryFn: () => api.get(`/public/resources/${slug}`).then((res) => res.data.data),
    enabled: !!slug,
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-xs font-mono">Loading guide...</div>;
  }

  if (error || !resource) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-serif font-bold text-slate-900">Resource Not Found</h2>
        <Link to="/resources" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to resources
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'Resources', href: '/resources' },
            { label: resource.title },
          ]}
        />
      </div>

      {/* Header */}
      <div className="space-y-4">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-700 font-bold">
          {resource.category.replace('_', ' ')}
        </span>
        <h1 className="text-3xl sm:text-5xl font-serif font-bold text-editorial-dark leading-tight">
          {resource.title}
        </h1>
        <p className="text-base sm:text-lg text-slate-600 font-sans leading-relaxed">
          {resource.summary}
        </p>
        <div className="flex items-center gap-4 text-xs text-slate-500 font-mono pt-2 border-b border-slate-200 pb-4">
          <span>By {resource.author}</span>
          <span>&bull;</span>
          <span>For The Truth Publishing Editorial Archive</span>
        </div>
      </div>

      {/* Main Content Article */}
      <article className="p-8 sm:p-12 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6 text-slate-700 leading-relaxed text-sm sm:text-base font-sans whitespace-pre-line">
        {resource.content}
      </article>

      {/* Footer CTA */}
      <div className="p-8 rounded-2xl bg-editorial-parchment border border-brand-200 text-center space-y-4">
        <h3 className="font-serif text-xl font-bold text-editorial-dark">Have Questions Regarding Your Manuscript?</h3>
        <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
          Our senior editors are available to evaluate your proposal or answer specific style guide queries.
        </p>
        <Link
          to="/submit-enquiry"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs"
        >
          Submit a Manuscript Query
        </Link>
      </div>

    </div>
  );
};
